/**
 * Scan POS P6 -- the Sale screen's handle on the Remote Scanner.
 *
 * Enabled only when BOTH hold:
 *   1. this build talks to the Staging API (realtime-config.ts), and
 *   2. the Backend's EFFECTIVE permissions for the signed-in employee
 *      (SESSION_PERMISSIONS) include `scanner.pair` -- never the Redux
 *      preview `user.permissions`.
 * Otherwise nothing is opened and no Scan POS call is made. A later 401/403
 * from any Scan POS call switches the integration off.
 *
 * The page hands in `lookup` / `mutate` / `onApplied`; they are kept in refs,
 * so a WebSocket callback always reaches the page's CURRENT functions and
 * state, never an old render's.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReceiverState, scanAck, scanPosApi } from '../api/scan-pos';
import { session_permissions } from '../auth/session-api';
import { isScanPosAvailable, resolveRealtimeUrl } from './realtime-config';
import { LookupOutcome, MutateOutcome, RejectReason, ScanEventReceiver } from './receiver';
import { SurfaceSession, SurfaceSnapshot } from './surface-session';
import { reportScanTiming } from './scan-timing';
import { FastBarcodeReceiver } from './fast-barcode-receiver';

export const SCANNER_PAIR = 'scanner.pair';

export interface ScanPosPageHooks {
    lookup(barcode: string): Promise<LookupOutcome>;
    mutate(product: any): MutateOutcome;
    onApplied?(product: any): void;
    onRejected?(reason: RejectReason): void;
    onAmbiguous?(): void;
    /** Asked when the surface becomes ACTIVE: may the page say READY now? */
    isPageIdle(): boolean;
}

export interface UseScanPos {
    enabled: boolean;
    snapshot: SurfaceSnapshot | null;
    setReceiverState(state: ReceiverState): void;
    transfer(): Promise<boolean>;
    retryChannel(): Promise<void>;
}

/** Test seam: the WebSocket constructor used for new sessions. */
export const realtimeSocketFactory: { ctor: any } = {
    ctor: typeof window !== 'undefined' ? (window as any).WebSocket : undefined,
};

export const useScanPos = (hooks: ScanPosPageHooks): UseScanPos => {
    const hooksRef = useRef(hooks);
    hooksRef.current = hooks;
    const sessionRef = useRef<SurfaceSession | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [snapshot, setSnapshot] = useState<SurfaceSnapshot | null>(null);

    useEffect(() => {
        let cancelled = false;
        const realtimeUrl = resolveRealtimeUrl();
        if (!isScanPosAvailable() || !realtimeUrl || !realtimeSocketFactory.ctor) return undefined;

        (async () => {
            let permissions: string[] = [];
            try {
                permissions = await session_permissions();
            } catch {
                return;                                          // unknown = not allowed
            }
            if (cancelled || !permissions.includes(SCANNER_PAIR)) return;

            let session: SurfaceSession | null = null;
            const receiver = new ScanEventReceiver({
                currentBinding: () => session?.currentBinding() ?? null,
                receiverState: () => session?.receiverState() ?? 'WAITING',
                lookup: (barcode) => hooksRef.current.lookup(barcode),
                mutate: (product) => hooksRef.current.mutate(product),
                sendAck: (body) => scanAck(body),
                onApplied: (product) => hooksRef.current.onApplied?.(product),
                onRejected: (reason) => hooksRef.current.onRejected?.(reason),
                onAmbiguous: () => hooksRef.current.onAmbiguous?.(),
                onTiming: reportScanTiming,
            });
            // Fast Wireless Barcode Path: BARCODE_SCAN -> the same lookup, Cart and
            // feedback hooks -> BARCODE_ACK on the SAME socket. Never scanAck().
            const fast = new FastBarcodeReceiver({
                currentBinding: () => session?.currentBinding() ?? null,
                receiverState: () => session?.receiverState() ?? 'WAITING',
                lookup: (barcode) => hooksRef.current.lookup(barcode),
                mutate: (product) => hooksRef.current.mutate(product),
                sendAck: (binding, requestId, result) => session?.sendFastBarcodeAck(binding, requestId, result) ?? false,
                onApplied: (product) => hooksRef.current.onApplied?.(product),
                onRejected: (reason) => hooksRef.current.onRejected?.(reason),
                onCheck: () => hooksRef.current.onAmbiguous?.(),
                onTiming: reportScanTiming,
            });
            session = new SurfaceSession({
                api: scanPosApi,
                realtimeUrl,
                WebSocketCtor: realtimeSocketFactory.ctor,
                onFrame: (frame, binding) => { void receiver.handle(frame, binding); },
                onFastFrame: (frame, binding) => { void fast.handle(frame, binding); },
                onChange: (snap) => { if (!cancelled) setSnapshot(snap); },
                onForbidden: () => { if (!cancelled) setEnabled(false); },
                onBecameActive: () => {
                    if (hooksRef.current.isPageIdle()) session?.setReceiverState('READY');
                },
            });
            sessionRef.current = session;
            setEnabled(true);
            await session.start();
        })();

        return () => {
            cancelled = true;
            sessionRef.current?.stop(true);
            sessionRef.current = null;
        };
    }, []);

    const setReceiverState = useCallback((state: ReceiverState) => {
        sessionRef.current?.setReceiverState(state);
    }, []);
    const transfer = useCallback(async () => (sessionRef.current ? sessionRef.current.transfer() : false), []);
    const retryChannel = useCallback(async () => { await sessionRef.current?.retryChannel(); }, []);

    return { enabled: enabled && !!snapshot?.available, snapshot, setReceiverState, transfer, retryChannel };
};
